import { z } from 'zod';

// Plataforma alvo do kill switch de versão. `/appConfig/{platform}` no
// Firestore guarda um doc por target. `web` cobre apps/app (e futuro
// admin web); `android` cobre o app nativo Kotlin; `ios` cobre o app nativo
// Swift (#106) — o doc `/appConfig/ios` só passa a existir no 1º release iOS.
//
// Why top-level (`/appConfig/...`) e não `/config/app/...`: Firestore
// alterna collection/doc, então `/config/app/web` exigiria nesting via
// subcollection (`/config/app/versions/web`). Promover a top-level deixa
// o path rasteiro e a regra `read: if true` mais óbvia.
export const APP_VERSION_CONFIG_COLLECTION = 'appConfig';
export const AppPlatformSchema = z.enum(['web', 'android', 'ios']);
export type AppPlatform = z.infer<typeof AppPlatformSchema>;

// Doc de controle de versão por plataforma — fonte da verdade para o
// gate de "atualize o app".
//
// `latestBuild` é o build mais recente publicado; clientes em build
// menor mostram banner não-bloqueante. `minBuild` é o piso suportado;
// clientes abaixo entram em tela bloqueante até atualizar (kill
// switch). Comparação é por inteiro do build (campo após `+` no
// formato `YYYY.MM.DD+BUILD` usado em `package.json#version` /
// versionCode do Android).
//
// `latestVersion` é só rótulo de UI ("Atualize para 2026.05.10+63") —
// nunca usado em decisão lógica.
//
// `updatedAt` é epoch ms (server-side em `Date.now()` no callable que
// publica) — útil para auditar quando o gate apertou.
export const AppVersionConfigSchema = z.object({
  latestBuild: z.number().int().nonnegative(),
  minBuild: z.number().int().nonnegative(),
  latestVersion: z.string().min(1),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type AppVersionConfig = z.infer<typeof AppVersionConfigSchema>;

// Helper para extrair o número de build do formato `YYYY.MM.DD+BUILD`
// (gerado por release.yml). Em dev local o pkg.version pode estar em
// `0.0.0` sem `+` — devolvemos 0 para não disparar gate em dev.
const BUILD_SUFFIX_RE = /\+(\d+)$/;
export function parseBuildNumber(version: string): number {
  const match = BUILD_SUFFIX_RE.exec(version);
  const captured = match?.[1];
  if (captured === undefined) return 0;
  const n = Number.parseInt(captured, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

// As plataformas que têm doc de kill switch em `/appConfig/{platform}`.
//
// Deliberadamente SEPARADO de `AppPlatformSchema`, e não uma extensão dele: os
// dois conjuntos só parecem iguais. `AppPlatform` é o vocabulário de push token
// e de device (`registerPushToken`), onde `admin` não existe e um valor novo
// precisaria atravessar o contrato Swift — que o declara fechado
// (`contract/targets.ts`, `readFallback: null`). Já `/appConfig/admin` existe em
// produção desde 2026-06-18 e o painel precisa listá-lo.
//
// É o mesmo argumento que impede unificar `AppPlatform` com `BaixaPlatform`:
// vocabulários que coincidem hoje, com donos diferentes.
export const AppVersionGatePlatformSchema = z.enum(['web', 'admin', 'android', 'ios']);
export type AppVersionGatePlatform = z.infer<typeof AppVersionGatePlatformSchema>;

// Input do callable `setAppVersionConfig` — a única porta para apertar o kill
// switch. O callable escreve SÓ o `minBuild`; `latestBuild`/`latestVersion` são
// fato publicado (release do web/admin/android, webhook da App Store no iOS) e
// não se editam à mão.
//
// `reason` é obrigatório porque isto trava todos os aparelhos de uma plataforma
// de uma vez: seis meses depois, "quem apertou" sem "por quê" não explica nada.
export const SetAppVersionConfigInputSchema = z.object({
  platform: AppVersionGatePlatformSchema,
  minBuild: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(200),
});
export type SetAppVersionConfigInput = z.infer<typeof SetAppVersionConfigInputSchema>;

export type SetAppVersionConfigResult = {
  platform: AppVersionGatePlatform;
  /** O valor que passou a valer. */
  minBuild: number;
  /** O que valia antes — é o que permite desfazer, e o que a tela ecoa. */
  previousMinBuild: number;
  /** O teto publicado no momento do aperto, para a faixa ficar legível. */
  latestBuild: number;
};
